import React, { useState, useEffect, useCallback } from 'react';
import {
  LogOut, Menu, X, Home, Wallet, ShoppingCart, Users, Building2, ChevronRight,
  Plus, RefreshCw, ArrowLeft, Save, Check, Clock, AlertTriangle, CreditCard,
  MapPin, Phone, Mail, IndianRupee, Package, CheckCircle2, XCircle, Eye,
  FlaskRound, Search, Download, Edit2, Trash2, Bell, FileSpreadsheet, Lock, KeyRound
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ═══ Login ═══ */
const FranchiseLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [reason, setReason] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/franchise/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Invalid credentials';
      if (status === 423) {
        setLocked(true);
      }
      toast.error(detail);
    }
    setLoading(false);
  };

  const handleResetRequest = async () => {
    if (!email) { toast.error('Enter your email first'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/franchise/request-password-reset`, { email, reason: reason || 'Account locked / forgot password' });
      setResetSent(true);
      toast.success('Reset request sent! Super Admin will review it.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset request');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#1E3A5F] to-[#0EA5E9] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-white/10 backdrop-blur-lg rounded-3xl flex items-center justify-center mx-auto mb-4 border border-white/20">
            <Building2 className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>AMC247</h1>
          <p className="text-white/60 text-sm mt-1">Franchise Portal</p>
        </div>

        {locked && !resetSent ? (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 space-y-4 border border-red-400/30">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock className="h-6 w-6 text-red-400" />
              </div>
              <h2 className="text-white font-bold text-lg">Account Locked</h2>
              <p className="text-white/60 text-sm mt-1">Too many failed login attempts. Request a password reset from Super Admin.</p>
            </div>
            <div>
              <Label className="text-white/80 text-sm">Your Email</Label>
              <Input value={email} onChange={e => setEmail(e.target.value)} className="bg-white/10 border-white/20 text-white h-11 rounded-xl mt-1" data-testid="reset-email" />
            </div>
            <div>
              <Label className="text-white/80 text-sm">Reason (optional)</Label>
              <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Forgot password" className="bg-white/10 border-white/20 text-white placeholder:text-white/30 h-11 rounded-xl mt-1" data-testid="reset-reason" />
            </div>
            <Button onClick={handleResetRequest} disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl h-12 font-bold" data-testid="request-reset-btn">
              {loading ? 'Sending...' : 'Request Password Reset'}
            </Button>
            <button onClick={() => { setLocked(false); setPassword(''); }} className="w-full text-white/50 text-sm hover:text-white/80">
              Back to Login
            </button>
          </div>
        ) : resetSent ? (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 space-y-4 border border-green-400/30 text-center">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="h-6 w-6 text-green-400" />
            </div>
            <h2 className="text-white font-bold text-lg">Reset Request Sent</h2>
            <p className="text-white/60 text-sm">Your Super Admin has been notified. They will review your request and set a new password for you.</p>
            <button onClick={() => { setResetSent(false); setLocked(false); setPassword(''); }} className="w-full text-white/50 text-sm hover:text-white/80 mt-4" data-testid="back-to-login">
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 space-y-4 border border-white/10">
            <div>
              <Label className="text-white/80 text-sm">Email / Username</Label>
              <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="bg-white/10 border-white/20 text-white placeholder:text-white/30 h-12 rounded-xl" required data-testid="franchise-email" />
            </div>
            <div>
              <Label className="text-white/80 text-sm">Password</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" className="bg-white/10 border-white/20 text-white placeholder:text-white/30 h-12 rounded-xl" required data-testid="franchise-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-white text-[#0A2540] hover:bg-white/90 rounded-xl h-12 font-bold" data-testid="franchise-login-submit">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <button type="button" onClick={() => { if (email) { setLocked(true); } else { toast.error('Enter your email first'); } }} className="w-full text-white/40 text-xs hover:text-white/60">
              Forgot Password?
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ═══ Super Admin Dashboard ═══ */
const SuperAdminDashboard = ({ stats, onNavigate }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Franchises', value: stats.total_franchises, icon: Building2, color: 'bg-purple-100 text-purple-700' },
    { label: 'Total Balance', value: `Rs.${(stats.total_balance || 0).toLocaleString()}`, icon: Wallet, color: 'bg-green-100 text-green-700' },
    { label: 'Pending Recharges', value: stats.pending_recharges, icon: CreditCard, color: 'bg-amber-100 text-amber-700' },
    { label: 'Total Staff', value: stats.total_staff, icon: Users, color: 'bg-blue-100 text-blue-700' },
    { label: 'Check-ins Today', value: stats.checkins_today, icon: MapPin, color: 'bg-teal-100 text-teal-700' },
    { label: 'Collections Today', value: stats.collections_today, icon: Package, color: 'bg-pink-100 text-pink-700' },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-2xl p-4 shadow-sm">
              <div className={`inline-flex p-2 rounded-lg mb-2 ${c.color}`}><Icon className="h-4 w-4" /></div>
              <p className="text-xl font-bold text-[#0A2540]">{c.value}</p>
              <p className="text-xs text-[#475569]">{c.label}</p>
            </div>
          );
        })}
      </div>
      {stats.franchises?.length > 0 && (
        <div>
          <h3 className="font-semibold text-[#0A2540] mb-2">Franchise Branches</h3>
          <div className="space-y-2">
            {stats.franchises.map(f => (
              <button key={f.id} onClick={() => onNavigate('branch-detail', f)} className="w-full text-left bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all flex items-center justify-between" data-testid={`franchise-${f.id}`}>
                <div>
                  <p className="font-bold text-[#0A2540]">{f.name}</p>
                  <p className="text-xs text-[#475569]">{f.branch_code} | {f.city}, {f.area}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-green-600">Rs.{(f.wallet_balance || 0).toLocaleString()}</span>
                  <ChevronRight className="h-4 w-4 text-[#94A3B8]" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══ Franchise Admin Dashboard ═══ */
const FranchiseAdminDashboard = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Wallet Balance', value: `Rs.${(stats.wallet_balance || 0).toLocaleString()}`, icon: Wallet, color: stats.wallet_balance < 500 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700' },
    { label: 'Credit Used', value: `Rs.${(stats.credit_this_month || 0).toLocaleString()}`, icon: IndianRupee, color: 'bg-blue-100 text-blue-700' },
    { label: 'Pending Recharges', value: stats.pending_recharges, icon: Clock, color: 'bg-amber-100 text-amber-700' },
    { label: 'Staff Count', value: stats.staff_count, icon: Users, color: 'bg-purple-100 text-purple-700' },
    { label: 'Check-ins Today', value: stats.checkins_today, icon: MapPin, color: 'bg-teal-100 text-teal-700' },
    { label: 'Collections Today', value: stats.collections_today, icon: Package, color: 'bg-pink-100 text-pink-700' },
  ];
  return (
    <div className="space-y-4">
      {stats.wallet_balance < 500 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 font-medium">Low wallet balance! Please recharge to continue operations.</p>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-2xl p-4 shadow-sm">
              <div className={`inline-flex p-2 rounded-lg mb-2 ${c.color}`}><Icon className="h-4 w-4" /></div>
              <p className="text-xl font-bold text-[#0A2540]">{c.value}</p>
              <p className="text-xs text-[#475569]">{c.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══ Create Franchise ═══ */
const CreateFranchise = ({ onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: '', branch_code: '', city: '', area: '', contact_person: '',
    phone: '', email: '', password: '', gst: '', bank_account: '', bank_ifsc: '',
    working_hours: '8:00 AM - 8:00 PM',
  });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const u = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/franchise/branches`, form, { withCredentials: true });
      setCreated(data);
      toast.success('Franchise created!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  if (created) {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-[#0A2540] mb-1">Franchise Created!</h3>
          <p className="text-sm text-[#475569] mb-4">{created.name} ({created.branch_code})</p>
          <div className="bg-white rounded-xl p-4 text-left space-y-2">
            <p className="text-xs text-[#94A3B8] uppercase font-bold">Admin Login Credentials</p>
            <div className="flex justify-between"><span className="text-sm text-[#475569]">Email:</span><span className="text-sm font-bold text-[#0A2540]">{created.admin_credentials?.email}</span></div>
            <div className="flex justify-between"><span className="text-sm text-[#475569]">Password:</span><span className="text-sm font-bold text-[#0A2540]">{created.admin_credentials?.password}</span></div>
          </div>
        </div>
        <Button onClick={onCancel} className="w-full bg-[#0A2540] rounded-xl h-12">Back to Dashboard</Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pb-6">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Franchise Name *</Label><Input value={form.name} onChange={e => u('name', e.target.value)} required placeholder="AMC Chennai Central" className="h-11 rounded-xl mt-1" data-testid="fr-name" /></div>
        <div><Label>Branch Code</Label><Input value={form.branch_code} onChange={e => u('branch_code', e.target.value)} placeholder="Auto-generated" className="h-11 rounded-xl mt-1" data-testid="fr-code" /></div>
        <div><Label>City *</Label><Input value={form.city} onChange={e => u('city', e.target.value)} required placeholder="Chennai" className="h-11 rounded-xl mt-1" data-testid="fr-city" /></div>
        <div><Label>Area</Label><Input value={form.area} onChange={e => u('area', e.target.value)} placeholder="T. Nagar" className="h-11 rounded-xl mt-1" /></div>
        <div><Label>Contact Person *</Label><Input value={form.contact_person} onChange={e => u('contact_person', e.target.value)} required placeholder="Name" className="h-11 rounded-xl mt-1" /></div>
        <div><Label>Phone *</Label><Input value={form.phone} onChange={e => u('phone', e.target.value)} required placeholder="9876543210" className="h-11 rounded-xl mt-1" /></div>
        <div><Label>Admin Email *</Label><Input value={form.email} onChange={e => u('email', e.target.value)} required placeholder="admin@branch.com" className="h-11 rounded-xl mt-1" data-testid="fr-email" /></div>
        <div><Label>Admin Password</Label><Input value={form.password} onChange={e => u('password', e.target.value)} placeholder="Auto-generated" className="h-11 rounded-xl mt-1" data-testid="fr-password" /></div>
        <div><Label>GST Number</Label><Input value={form.gst} onChange={e => u('gst', e.target.value)} placeholder="GSTIN" className="h-11 rounded-xl mt-1" /></div>
        <div><Label>Bank Account</Label><Input value={form.bank_account} onChange={e => u('bank_account', e.target.value)} placeholder="Account No." className="h-11 rounded-xl mt-1" /></div>
        <div><Label>IFSC</Label><Input value={form.bank_ifsc} onChange={e => u('bank_ifsc', e.target.value)} placeholder="IFSC Code" className="h-11 rounded-xl mt-1" /></div>
        <div><Label>Working Hours</Label><Input value={form.working_hours} onChange={e => u('working_hours', e.target.value)} className="h-11 rounded-xl mt-1" /></div>
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="button" onClick={onCancel} variant="outline" className="flex-1 h-12 rounded-xl">Cancel</Button>
        <Button type="submit" disabled={saving} className="flex-1 h-12 rounded-xl bg-[#0A2540]" data-testid="fr-create-submit">
          {saving ? 'Creating...' : 'Create Franchise'}
        </Button>
      </div>
    </form>
  );
};

/* ═══ Branch Profile ═══ */
const BranchProfile = ({ franchise, onSave, readOnly }) => {
  const [form, setForm] = useState(franchise || {});
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/franchise/branches/${franchise.id}`, form, { withCredentials: true });
      toast.success('Profile updated');
      if (onSave) onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pb-6">
      <div className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] rounded-2xl p-5 text-white">
        <p className="text-xs text-white/60 uppercase font-bold">{form.branch_code}</p>
        <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{form.name}</h3>
        <p className="text-sm text-white/70">{form.city}{form.area ? `, ${form.area}` : ''}</p>
        <p className="text-lg font-bold mt-2 text-green-300">Wallet: Rs.{(form.wallet_balance || 0).toLocaleString()}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Name</Label><Input value={form.name || ''} onChange={e => u('name', e.target.value)} className="h-11 rounded-xl mt-1" readOnly={readOnly} /></div>
        <div><Label>City</Label><Input value={form.city || ''} onChange={e => u('city', e.target.value)} className="h-11 rounded-xl mt-1" readOnly={readOnly} /></div>
        <div><Label>Area</Label><Input value={form.area || ''} onChange={e => u('area', e.target.value)} className="h-11 rounded-xl mt-1" readOnly={readOnly} /></div>
        <div><Label>Contact Person</Label><Input value={form.contact_person || ''} onChange={e => u('contact_person', e.target.value)} className="h-11 rounded-xl mt-1" readOnly={readOnly} /></div>
        <div><Label>Phone</Label><Input value={form.phone || ''} onChange={e => u('phone', e.target.value)} className="h-11 rounded-xl mt-1" readOnly={readOnly} /></div>
        <div><Label>Email</Label><Input value={form.email || ''} className="h-11 rounded-xl mt-1" readOnly /></div>
        <div><Label>GST</Label><Input value={form.gst || ''} onChange={e => u('gst', e.target.value)} className="h-11 rounded-xl mt-1" readOnly={readOnly} /></div>
        <div><Label>Bank Account</Label><Input value={form.bank_account || ''} onChange={e => u('bank_account', e.target.value)} className="h-11 rounded-xl mt-1" readOnly={readOnly} /></div>
        <div><Label>IFSC</Label><Input value={form.bank_ifsc || ''} onChange={e => u('bank_ifsc', e.target.value)} className="h-11 rounded-xl mt-1" readOnly={readOnly} /></div>
        <div className="col-span-2"><Label>Working Hours</Label><Input value={form.working_hours || ''} onChange={e => u('working_hours', e.target.value)} className="h-11 rounded-xl mt-1" readOnly={readOnly} /></div>
      </div>
      {!readOnly && (
        <Button type="submit" disabled={saving} className="w-full h-12 rounded-xl bg-[#0A2540]" data-testid="profile-save">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      )}
    </form>
  );
};

/* ═══ Wallet View ═══ */
const WalletView = ({ user, franchiseId }) => {
  const [recharges, setRecharges] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount: '', payment_method: 'UPI', payment_ref: '', payment_proof: '' });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const isSuper = user.role === 'super_admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, tRes] = await Promise.all([
        axios.get(`${API}/franchise/wallet/recharges`, { withCredentials: true }),
        axios.get(`${API}/franchise/wallet/transactions`, { withCredentials: true }),
      ]);
      setRecharges(rRes.data);
      setTransactions(tRes.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRechargeRequest = async (e) => {
    e.preventDefault();
    if (!form.payment_ref.trim()) {
      toast.error('Payment reference number is required');
      return;
    }
    try {
      await axios.post(`${API}/franchise/wallet/recharge-request`, { ...form, franchise_id: franchiseId }, { withCredentials: true });
      toast.success('Recharge request submitted');
      setShowForm(false);
      setForm({ amount: '', payment_method: 'UPI', payment_ref: '', payment_proof: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/franchise/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(p => ({ ...p, payment_proof: data.url }));
      toast.success('Proof uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleApprove = async (id, status) => {
    try {
      await axios.put(`${API}/franchise/wallet/recharges/${id}`, { status }, { withCredentials: true });
      toast.success(`Recharge ${status}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      {!isSuper && (
        <Button onClick={() => setShowForm(!showForm)} className="w-full bg-[#0A2540] rounded-xl h-12" data-testid="recharge-btn">
          <Plus className="h-4 w-4 mr-2" /> Request Recharge
        </Button>
      )}
      {showForm && (
        <form onSubmit={handleRechargeRequest} className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
          <div><Label>Amount (Rs.)</Label><Input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} required min="100" placeholder="1000" className="h-11 rounded-xl mt-1" data-testid="recharge-amount" /></div>
          <div><Label>Payment Method</Label>
            <select className="w-full h-11 border rounded-xl px-3 text-sm mt-1" value={form.payment_method} onChange={e => setForm(p => ({ ...p, payment_method: e.target.value }))}>
              <option value="UPI">UPI</option><option value="NEFT">NEFT</option><option value="Cash">Cash</option>
            </select>
          </div>
          <div><Label>Payment Reference / UTR No. *</Label><Input value={form.payment_ref} onChange={e => setForm(p => ({ ...p, payment_ref: e.target.value }))} required placeholder="UTR / Txn ID" className="h-11 rounded-xl mt-1" data-testid="recharge-ref" /></div>
          <div>
            <Label>Payment Proof (Screenshot)</Label>
            <input type="file" accept="image/*,.pdf" onChange={handleFileUpload} className="w-full mt-1 text-sm file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-[#F1F5F9] file:text-sm file:font-medium" data-testid="recharge-proof" />
            {uploading && <p className="text-xs text-[#0EA5E9] mt-1">Uploading...</p>}
            {form.payment_proof && <p className="text-xs text-green-600 mt-1">Proof attached</p>}
          </div>
          <Button type="submit" className="w-full bg-green-600 rounded-xl h-11" data-testid="recharge-submit">Submit Request</Button>
        </form>
      )}

      <div>
        <h3 className="font-semibold text-[#0A2540] mb-2">Recharge Requests</h3>
        {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : recharges.length === 0 ? <p className="text-sm text-[#94A3B8]">No requests yet</p> : (
          <div className="space-y-2">
            {recharges.map(r => (
              <div key={r.id} className="bg-white rounded-xl p-3 shadow-sm flex items-center justify-between">
                <div>
                  <p className="font-bold text-[#0A2540]">Rs.{(r.amount || 0).toLocaleString()}</p>
                  <p className="text-xs text-[#475569]">{r.payment_method} | Ref: {r.payment_ref || 'No ref'}</p>
                  {r.payment_proof && <a href={r.payment_proof} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0EA5E9] font-medium hover:underline">View Proof</a>}
                  <p className="text-xs text-[#94A3B8]">{r.created_at?.split('T')[0]}</p>
                </div>
                <div className="flex items-center gap-2">
                  {r.status === 'pending' && isSuper ? (
                    <>
                      <button onClick={() => handleApprove(r.id, 'approved')} className="bg-green-100 text-green-700 px-3 py-1 rounded-lg text-xs font-bold" data-testid={`approve-${r.id}`}>Approve</button>
                      <button onClick={() => handleApprove(r.id, 'rejected')} className="bg-red-100 text-red-700 px-3 py-1 rounded-lg text-xs font-bold">Reject</button>
                    </>
                  ) : (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-semibold text-[#0A2540] mb-2">Transactions</h3>
        {transactions.length === 0 ? <p className="text-sm text-[#94A3B8]">No transactions</p> : (
          <div className="space-y-2">
            {transactions.map(t => (
              <div key={t.id} className="bg-white rounded-xl p-3 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#0A2540]">{t.description}</p>
                  <p className="text-xs text-[#94A3B8]">{t.created_at?.split('T')[0]}</p>
                </div>
                <span className={`font-bold text-sm ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                  {t.type === 'credit' ? '+' : '-'}Rs.{(t.amount || 0).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══ Staff Management ═══ */
const StaffView = ({ user }) => {
  const [staff, setStaff] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', designation: 'Phlebotomist' });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/franchise/staff`, { withCredentials: true });
      setStaff(data);
    } catch { toast.error('Failed'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/franchise/staff`, form, { withCredentials: true });
      toast.success(`Staff created! Password: ${data.password}`);
      setShowForm(false);
      setForm({ name: '', email: '', phone: '', password: '', designation: 'Phlebotomist' });
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditForm({ name: s.name, email: s.email, phone: s.phone || '', designation: s.designation || 'Phlebotomist', password: '' });
  };

  const handleUpdate = async (staffId) => {
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/franchise/staff/${staffId}`, payload, { withCredentials: true });
      toast.success('Staff updated');
      setEditingId(null);
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete ${s.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/franchise/staff/${s.id}`, { withCredentials: true });
      toast.success('Staff deleted');
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const toggleStatus = async (s) => {
    const newStatus = s.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/franchise/staff/${s.id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Staff ${newStatus}`);
      fetchStaff();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => { setShowForm(!showForm); setEditingId(null); }} className="w-full bg-[#0A2540] rounded-xl h-12" data-testid="add-staff-btn">
        <Plus className="h-4 w-4 mr-2" /> Add Staff
      </Button>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required placeholder="Full name" className="h-11 rounded-xl mt-1" data-testid="staff-name" /></div>
          <div><Label>Email *</Label><Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required placeholder="staff@email.com" className="h-11 rounded-xl mt-1" data-testid="staff-email" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="9876543210" className="h-11 rounded-xl mt-1" /></div>
          <div><Label>Password</Label><Input value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Default: amc@staff123" className="h-11 rounded-xl mt-1" /></div>
          <div><Label>Designation</Label>
            <select className="w-full h-11 border rounded-xl px-3 text-sm mt-1" value={form.designation} onChange={e => setForm(p => ({ ...p, designation: e.target.value }))}>
              <option value="Phlebotomist">Phlebotomist</option><option value="Lab Tech">Lab Tech</option><option value="Driver">Driver</option><option value="Coordinator">Coordinator</option>
            </select>
          </div>
          <Button type="submit" className="w-full bg-green-600 rounded-xl h-11" data-testid="staff-create-submit">Create Staff</Button>
        </form>
      )}

      {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : staff.length === 0 ? <p className="text-sm text-[#94A3B8]">No staff added yet</p> : (
        <div className="space-y-2">
          {staff.map(s => (
            <div key={s.id} className="bg-white rounded-xl p-4 shadow-sm" data-testid={`staff-card-${s.id}`}>
              {editingId === s.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Name</Label><Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" data-testid="edit-staff-name" /></div>
                    <div><Label className="text-xs">Email</Label><Input value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" data-testid="edit-staff-email" /></div>
                    <div><Label className="text-xs">Phone</Label><Input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" /></div>
                    <div><Label className="text-xs">Password</Label><Input type="password" value={editForm.password} onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))} placeholder="Leave blank to keep" className="h-10 rounded-xl mt-0.5 text-sm" data-testid="edit-staff-password" /></div>
                  </div>
                  <div><Label className="text-xs">Designation</Label>
                    <select className="w-full h-10 border rounded-xl px-3 text-sm mt-0.5" value={editForm.designation} onChange={e => setEditForm(p => ({ ...p, designation: e.target.value }))}>
                      <option value="Phlebotomist">Phlebotomist</option><option value="Lab Tech">Lab Tech</option><option value="Driver">Driver</option><option value="Coordinator">Coordinator</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleUpdate(s.id)} className="bg-green-600 rounded-xl h-9 px-4 text-sm" data-testid="save-staff-edit">Save</Button>
                    <Button variant="outline" onClick={() => setEditingId(null)} className="rounded-xl h-9 px-4 text-sm">Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-[#0A2540]">{s.name}</p>
                    <p className="text-xs text-[#475569]">{s.designation} | {s.email}</p>
                    {s.phone && <p className="text-xs text-[#94A3B8]">{s.phone}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => startEdit(s)} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100" data-testid={`edit-staff-${s.id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDelete(s)} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100" data-testid={`delete-staff-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => toggleStatus(s)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-testid={`toggle-staff-${s.id}`}>
                      {s.status === 'active' ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══ All Branch Profiles (Super Admin) ═══ */
const AllBranchProfiles = () => {
  const [franchises, setFranchises] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/franchise/branches`, { withCredentials: true });
        setFranchises(data);
      } catch { toast.error('Failed'); }
      setLoading(false);
    })();
  }, []);

  if (selected) {
    return (
      <div>
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-[#475569] text-sm mb-3 hover:text-[#0A2540]"><ArrowLeft className="h-4 w-4" /> Back</button>
        <BranchProfile franchise={selected} onSave={() => { setSelected(null); }} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-[#0A2540]">All Franchise Branches</h3>
      {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : franchises.length === 0 ? <p className="text-sm text-[#94A3B8]">No franchises</p> : (
        franchises.map(f => (
          <button key={f.id} onClick={() => setSelected(f)} className="w-full text-left bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all flex items-center justify-between">
            <div>
              <p className="font-bold text-[#0A2540]">{f.name}</p>
              <p className="text-xs text-[#475569]">{f.branch_code} | {f.city}, {f.area}</p>
              <p className="text-xs text-[#94A3B8]">{f.email} | {f.phone}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-green-600">Rs.{(f.wallet_balance || 0).toLocaleString()}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${f.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{f.status}</span>
            </div>
          </button>
        ))
      )}
    </div>
  );
};

/* ═══ Test Catalog (Browse & Order) ═══ */
const TestCatalog = ({ user }) => {
  const [tests, setTests] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('tests');
  const [cart, setCart] = useState([]);
  const [ordering, setOrdering] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ test_name: '', category: '', b2b_cost: '', mrp: '', sample_requirement: '', tat: '' });
  const isSuper = user.role === 'super_admin';
  const isAdmin = user.role === 'franchise_admin';
  const isStaff = user.role === 'franchise_staff';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        axios.get(`${API}/franchise/catalog/tests`, { withCredentials: true }),
        axios.get(`${API}/franchise/catalog/profiles`, { withCredentials: true }),
      ]);
      setTests(tRes.data);
      setProfiles(pRes.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addToCart = (item) => {
    const existing = cart.find(c => c.id === item.id);
    if (existing) {
      setCart(cart.map(c => c.id === item.id ? { ...c, qty: c.qty + 1 } : c));
    } else {
      setCart([...cart, { ...item, qty: 1 }]);
    }
    toast.success(`${item.test_name} added`);
  };

  const removeFromCart = (id) => setCart(cart.filter(c => c.id !== id));
  const updateQty = (id, qty) => { if (qty < 1) return; setCart(cart.map(c => c.id === id ? { ...c, qty } : c)); };
  const cartTotal = cart.reduce((s, c) => s + c.price * c.qty, 0);

  const placeOrder = async () => {
    if (cart.length === 0) return;
    setOrdering(true);
    try {
      const items = cart.map(c => ({ test_name: c.test_name, price: c.price, qty: c.qty }));
      await axios.post(`${API}/franchise/orders`, { order_type: 'test', items }, { withCredentials: true });
      toast.success(isStaff ? 'Order placed! Pending admin approval.' : 'Test order placed! Wallet debited.');
      setCart([]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Order failed'); }
    setOrdering(false);
  };

  const handleSaveEdit = async (item) => {
    try {
      if (item.type === 'profile') {
        await axios.put(`${API}/franchise/catalog/profiles/${item.id}`, {
          name: item.test_name, category: item.category,
          b2b_cost: item.price, selling_price: item.mrp,
          primary_use: item.primary_use || '',
        }, { withCredentials: true });
      } else {
        await axios.put(`${API}/franchise/catalog/tests/${item.id}`, {
          test_name: item.test_name, category: item.category,
          b2b_cost: item.price, mrp: item.mrp,
          sample_requirement: item.sample_requirement || '', tat: item.tat || '',
        }, { withCredentials: true });
      }
      toast.success('Updated');
      setEditingTest(null);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/franchise/catalog/export`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', 'AMC247_Catalog_Export.xlsx');
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('Catalog exported');
    } catch { toast.error('Export failed'); }
  };

  const handleAddTest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/franchise/catalog/tests`, addForm, { withCredentials: true });
      toast.success('Test added');
      setShowAddForm(false);
      setAddForm({ test_name: '', category: '', b2b_cost: '', mrp: '', sample_requirement: '', tat: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this test?')) return;
    try {
      await axios.delete(`${API}/franchise/catalog/tests/${id}`, { withCredentials: true });
      toast.success('Deactivated');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const currentItems = tab === 'tests' ? tests : profiles;
  const filtered = currentItems.filter(t => !search || t.test_name?.toLowerCase().includes(search.toLowerCase()) || t.category?.toLowerCase().includes(search.toLowerCase()));
  const grouped = {};
  filtered.forEach(t => { if (!grouped[t.category]) grouped[t.category] = []; grouped[t.category].push(t); });

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-2 bg-[#F1F5F9] p-1 rounded-xl">
        <button onClick={() => setTab('tests')} className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-colors ${tab === 'tests' ? 'bg-[#0A2540] text-white' : 'text-[#475569]'}`} data-testid="tab-tests">
          Lab Tests ({tests.length})
        </button>
        <button onClick={() => setTab('profiles')} className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-colors ${tab === 'profiles' ? 'bg-[#0A2540] text-white' : 'text-[#475569]'}`} data-testid="tab-profiles">
          Health Profiles ({profiles.length})
        </button>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
          <Input placeholder={`Search ${tab}...`} className="pl-10 h-11 rounded-xl" value={search} onChange={e => setSearch(e.target.value)} data-testid="test-search" />
        </div>
        {isSuper && tab === 'tests' && (
          <Button onClick={() => setShowAddForm(!showAddForm)} className="bg-[#0A2540] rounded-xl h-11 px-4" data-testid="add-test-btn">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        )}
        <Button onClick={handleExportExcel} variant="outline" className="rounded-xl h-11 px-4 border-green-300 text-green-700 hover:bg-green-50" data-testid="export-catalog-btn">
          <Download className="h-4 w-4 mr-1" /> Excel
        </Button>
        <Button onClick={fetchData} variant="outline" size="icon" className="rounded-xl"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {/* Add Test Form (Super Admin) */}
      {isSuper && showAddForm && tab === 'tests' && (
        <form onSubmit={handleAddTest} className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Test Name *</Label><Input value={addForm.test_name} onChange={e => setAddForm(p => ({ ...p, test_name: e.target.value }))} required className="h-10 rounded-xl mt-1 text-sm" /></div>
            <div><Label className="text-xs">Category *</Label><Input value={addForm.category} onChange={e => setAddForm(p => ({ ...p, category: e.target.value }))} required className="h-10 rounded-xl mt-1 text-sm" /></div>
            <div><Label className="text-xs">B2B Cost *</Label><Input type="number" value={addForm.b2b_cost} onChange={e => setAddForm(p => ({ ...p, b2b_cost: e.target.value }))} required className="h-10 rounded-xl mt-1 text-sm" /></div>
            <div><Label className="text-xs">MRP (B2C) *</Label><Input type="number" value={addForm.mrp} onChange={e => setAddForm(p => ({ ...p, mrp: e.target.value }))} required className="h-10 rounded-xl mt-1 text-sm" /></div>
            <div><Label className="text-xs">Sample</Label><Input value={addForm.sample_requirement} onChange={e => setAddForm(p => ({ ...p, sample_requirement: e.target.value }))} placeholder="e.g. WB-EDTA" className="h-10 rounded-xl mt-1 text-sm" /></div>
            <div><Label className="text-xs">TAT</Label><Input value={addForm.tat} onChange={e => setAddForm(p => ({ ...p, tat: e.target.value }))} placeholder="e.g. 4 hrs" className="h-10 rounded-xl mt-1 text-sm" /></div>
          </div>
          <Button type="submit" className="w-full bg-green-600 rounded-xl h-10 text-sm">Save Test</Button>
        </form>
      )}

      {/* Cart */}
      {!isSuper && cart.length > 0 && (
        <div className="bg-[#0A2540] rounded-2xl p-4 text-white" data-testid="test-cart">
          <h3 className="font-bold mb-2 flex items-center justify-between"><span>Cart ({cart.length})</span><span className="text-green-300">Rs.{cartTotal.toLocaleString()}</span></h3>
          <div className="space-y-1 max-h-40 overflow-y-auto mb-3">
            {cart.map(c => (
              <div key={c.id} className="flex items-center justify-between text-sm">
                <span className="text-white/80 truncate flex-1 mr-2">{c.test_name}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => updateQty(c.id, c.qty - 1)} className="bg-white/20 px-2 rounded">-</button>
                  <span className="w-6 text-center">{c.qty}</span>
                  <button onClick={() => updateQty(c.id, c.qty + 1)} className="bg-white/20 px-2 rounded">+</button>
                  <span className="w-16 text-right">Rs.{(c.price * c.qty).toLocaleString()}</span>
                  <button onClick={() => removeFromCart(c.id)} className="text-red-300 ml-1"><X className="h-3 w-3" /></button>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={placeOrder} disabled={ordering} className="w-full bg-green-600 hover:bg-green-700 rounded-xl h-11" data-testid="place-test-order">
            {ordering ? 'Placing...' : `Place Order - Rs.${cartTotal.toLocaleString()}`}
          </Button>
        </div>
      )}

      {/* Table Header */}
      <div className="bg-[#0A2540] text-white rounded-xl px-4 py-2 text-xs font-bold grid grid-cols-12 gap-1">
        <span className="col-span-4">{tab === 'tests' ? 'Test Name' : 'Profile Name'}</span>
        <span className="col-span-2 text-right">B2B Cost</span>
        <span className="col-span-2 text-right">{tab === 'tests' ? 'MRP' : 'Selling'}</span>
        <span className="col-span-2 text-right">Margin %</span>
        <span className="col-span-2 text-right">Profit</span>
      </div>

      {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : Object.keys(grouped).length === 0 ? <p className="text-sm text-[#94A3B8]">No {tab} found</p> : (
        Object.entries(grouped).map(([cat, catItems]) => (
          <div key={cat}>
            <h3 className="font-semibold text-[#0A2540] text-xs mb-1 mt-3 sticky top-0 bg-[#F1F5F9] py-1 uppercase tracking-wider">{cat} ({catItems.length})</h3>
            <div className="space-y-1">
              {catItems.map(t => (
                editingTest?.id === t.id ? (
                  <div key={t.id} className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <Input value={editingTest.test_name} onChange={e => setEditingTest(p => ({ ...p, test_name: e.target.value }))} placeholder="Name" className="h-9 rounded-lg text-sm" />
                      <Input value={editingTest.category} onChange={e => setEditingTest(p => ({ ...p, category: e.target.value }))} placeholder="Category" className="h-9 rounded-lg text-sm" />
                      <Input type="number" value={editingTest.price} onChange={e => setEditingTest(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} placeholder="B2B Cost" className="h-9 rounded-lg text-sm" />
                      <Input type="number" value={editingTest.mrp} onChange={e => setEditingTest(p => ({ ...p, mrp: parseFloat(e.target.value) || 0 }))} placeholder={t.type === 'profile' ? 'Selling Price' : 'MRP'} className="h-9 rounded-lg text-sm" />
                      {t.type === 'profile' ? (
                        <Input value={editingTest.primary_use || ''} onChange={e => setEditingTest(p => ({ ...p, primary_use: e.target.value }))} placeholder="Primary Use" className="h-9 rounded-lg text-sm col-span-2" />
                      ) : (
                        <>
                          <Input value={editingTest.sample_requirement || ''} onChange={e => setEditingTest(p => ({ ...p, sample_requirement: e.target.value }))} placeholder="Sample" className="h-9 rounded-lg text-sm" />
                          <Input value={editingTest.tat || ''} onChange={e => setEditingTest(p => ({ ...p, tat: e.target.value }))} placeholder="TAT" className="h-9 rounded-lg text-sm" />
                        </>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleSaveEdit(editingTest)} className="flex-1 bg-green-600 h-9 rounded-lg text-xs">Save</Button>
                      <Button onClick={() => setEditingTest(null)} variant="outline" className="h-9 rounded-lg text-xs">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div key={t.id} className="bg-white rounded-xl px-4 py-2.5 grid grid-cols-12 gap-1 items-center shadow-sm text-sm" data-testid={`catalog-item-${t.id}`}>
                    <div className="col-span-4">
                      <span className="font-medium text-[#0A2540] truncate block">{t.test_name}</span>
                      <span className="text-[10px] text-[#94A3B8]">
                        {t.type === 'profile' ? `${t.tests_count} tests` : ''}
                        {t.sample_requirement ? ` ${t.sample_requirement}` : ''}
                        {t.tat ? ` | ${t.tat}` : ''}
                      </span>
                    </div>
                    <span className="col-span-2 text-right font-bold text-[#0A2540]">Rs.{Number(t.price || 0).toLocaleString('en-IN')}</span>
                    <span className="col-span-2 text-right text-[#475569]">Rs.{Number(t.mrp || 0).toLocaleString('en-IN')}</span>
                    <span className={`col-span-2 text-right font-bold ${(t.gross_margin || 0) >= 30 ? 'text-green-600' : (t.gross_margin || 0) >= 15 ? 'text-amber-600' : 'text-red-500'}`}>
                      {t.gross_margin != null ? `${t.gross_margin}%` : '-'}
                    </span>
                    <div className="col-span-2 flex items-center justify-end gap-1">
                      <span className={`font-bold text-xs ${(t.profit || 0) > 0 ? 'text-green-600' : 'text-red-500'}`}>Rs.{t.profit || 0}</span>
                      {isSuper && (
                        <button onClick={() => setEditingTest({ ...t })} className="text-[#0EA5E9] hover:bg-blue-50 p-1 rounded" data-testid={`edit-test-${t.id}`}><Edit2 className="h-3 w-3" /></button>
                      )}
                      {isSuper && t.type !== 'profile' && (
                        <button onClick={() => handleDelete(t.id)} className="text-red-400 hover:bg-red-50 p-1 rounded"><Trash2 className="h-3 w-3" /></button>
                      )}
                      {!isSuper && <button onClick={() => addToCart(t)} className="bg-[#0EA5E9] text-white text-xs px-2 py-1 rounded-lg font-bold ml-1" data-testid={`add-test-${t.id}`}><Plus className="h-3 w-3" /></button>}
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

/* ═══ Consumables Store ═══ */
const ConsumablesStore = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [ordering, setOrdering] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ item_name: '', category: '', usage: '', price: '', unit: 'each' });
  const isSuper = user.role === 'super_admin';
  const isStaff = user.role === 'franchise_staff';

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/franchise/catalog/consumables`, { withCredentials: true });
      setItems(data);
    } catch { toast.error('Failed'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const addToCart = (item) => {
    const existing = cart.find(c => c.id === item.id);
    if (existing) {
      setCart(cart.map(c => c.id === item.id ? { ...c, qty: c.qty + 1 } : c));
    } else {
      setCart([...cart, { ...item, qty: 1 }]);
    }
    toast.success(`${item.item_name} added`);
  };

  const removeFromCart = (id) => setCart(cart.filter(c => c.id !== id));
  const updateQty = (id, qty) => { if (qty < 1) return; setCart(cart.map(c => c.id === id ? { ...c, qty } : c)); };
  const cartTotal = cart.reduce((s, c) => s + c.price * c.qty, 0);

  const placeOrder = async () => {
    if (cart.length === 0) return;
    setOrdering(true);
    try {
      const orderItems = cart.map(c => ({ item_name: c.item_name, price: c.price, qty: c.qty, unit: c.unit }));
      await axios.post(`${API}/franchise/orders`, { order_type: 'consumable', items: orderItems }, { withCredentials: true });
      toast.success(isStaff ? 'Order placed! Pending admin approval.' : 'Consumable order placed! Wallet debited.');
      setCart([]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Order failed'); }
    setOrdering(false);
  };

  const handleSaveEdit = async (item) => {
    try {
      await axios.put(`${API}/franchise/catalog/consumables/${item.id}`, { item_name: item.item_name, category: item.category, usage: item.usage, price: item.price, unit: item.unit }, { withCredentials: true });
      toast.success('Updated');
      setEditingItem(null);
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/franchise/catalog/consumables/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/franchise/catalog/consumables`, addForm, { withCredentials: true });
      toast.success('Item added');
      setShowAddForm(false);
      setAddForm({ item_name: '', category: '', usage: '', price: '', unit: 'each' });
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  const grouped = {};
  items.forEach(i => { if (!grouped[i.category]) grouped[i.category] = []; grouped[i.category].push(i); });

  return (
    <div className="space-y-4">
      {/* Admin: Add button */}
      {isSuper && (
        <Button onClick={() => setShowAddForm(!showAddForm)} className="w-full bg-[#0A2540] rounded-xl h-11" data-testid="add-consumable-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Consumable
        </Button>
      )}

      {isSuper && showAddForm && (
        <form onSubmit={handleAddItem} className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Item Name *</Label><Input value={addForm.item_name} onChange={e => setAddForm(p => ({ ...p, item_name: e.target.value }))} required className="h-10 rounded-xl mt-1" data-testid="cons-add-name" /></div>
            <div><Label>Category *</Label><Input value={addForm.category} onChange={e => setAddForm(p => ({ ...p, category: e.target.value }))} required className="h-10 rounded-xl mt-1" /></div>
            <div><Label>Usage</Label><Input value={addForm.usage} onChange={e => setAddForm(p => ({ ...p, usage: e.target.value }))} className="h-10 rounded-xl mt-1" /></div>
            <div><Label>Price (Rs.)</Label><Input type="number" value={addForm.price} onChange={e => setAddForm(p => ({ ...p, price: e.target.value }))} className="h-10 rounded-xl mt-1" /></div>
            <div><Label>Unit</Label>
              <select className="w-full h-10 border rounded-xl px-3 text-sm mt-1" value={addForm.unit} onChange={e => setAddForm(p => ({ ...p, unit: e.target.value }))}>
                <option value="each">each</option><option value="box of 100">box of 100</option><option value="roll">roll</option><option value="kit">kit</option><option value="pack">pack</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="bg-green-600 rounded-xl h-10">Save</Button>
            <Button type="button" onClick={() => setShowAddForm(false)} variant="outline" className="rounded-xl h-10">Cancel</Button>
          </div>
        </form>
      )}

      {/* Cart for franchise admin/staff */}
      {!isSuper && cart.length > 0 && (
        <div className="bg-[#0A2540] rounded-2xl p-4 text-white" data-testid="consumable-cart">
          <h3 className="font-bold mb-2 flex items-center justify-between"><span>Cart ({cart.length})</span><span className="text-green-300">Rs.{cartTotal.toLocaleString()}</span></h3>
          <div className="space-y-1 max-h-40 overflow-y-auto mb-3">
            {cart.map(c => (
              <div key={c.id} className="flex items-center justify-between text-sm">
                <span className="text-white/80 truncate flex-1 mr-2">{c.item_name}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => updateQty(c.id, c.qty - 1)} className="bg-white/20 px-2 rounded">-</button>
                  <span className="w-6 text-center">{c.qty}</span>
                  <button onClick={() => updateQty(c.id, c.qty + 1)} className="bg-white/20 px-2 rounded">+</button>
                  <span className="w-16 text-right">Rs.{(c.price * c.qty).toLocaleString()}</span>
                  <button onClick={() => removeFromCart(c.id)} className="text-red-300 ml-1"><X className="h-3 w-3" /></button>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={placeOrder} disabled={ordering} className="w-full bg-green-600 hover:bg-green-700 rounded-xl h-11" data-testid="place-consumable-order">
            {ordering ? 'Placing...' : `Place Order - Rs.${cartTotal.toLocaleString()}`}
          </Button>
        </div>
      )}

      {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : (
        Object.entries(grouped).map(([cat, catItems]) => (
          <div key={cat}>
            <h3 className="font-semibold text-[#0A2540] text-sm mb-2 sticky top-0 bg-[#F1F5F9] py-1">{cat} ({catItems.length})</h3>
            <div className="space-y-1">
              {catItems.map(item => (
                editingItem?.id === item.id ? (
                  <div key={item.id} className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={editingItem.item_name} onChange={e => setEditingItem(p => ({ ...p, item_name: e.target.value }))} placeholder="Name" className="h-9 rounded-lg text-sm" />
                      <Input value={editingItem.usage} onChange={e => setEditingItem(p => ({ ...p, usage: e.target.value }))} placeholder="Usage" className="h-9 rounded-lg text-sm" />
                      <Input type="number" value={editingItem.price} onChange={e => setEditingItem(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} placeholder="Price" className="h-9 rounded-lg text-sm" />
                      <select className="h-9 border rounded-lg px-2 text-sm" value={editingItem.unit} onChange={e => setEditingItem(p => ({ ...p, unit: e.target.value }))}>
                        <option value="each">each</option><option value="box of 100">box of 100</option><option value="roll">roll</option><option value="kit">kit</option><option value="pack">pack</option>
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleSaveEdit(editingItem)} className="flex-1 bg-green-600 h-8 rounded-lg text-xs">Save</Button>
                      <Button onClick={() => setEditingItem(null)} variant="outline" className="h-8 rounded-lg text-xs">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div key={item.id} className="bg-white rounded-xl px-4 py-3 flex items-center justify-between shadow-sm">
                    <div className="flex-1 mr-2">
                      <p className="text-sm font-medium text-[#0A2540]">{item.item_name}</p>
                      <p className="text-xs text-[#94A3B8]">{item.usage} | {item.unit}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#0A2540]">{item.price > 0 ? `Rs.${item.price}` : 'Free'}</span>
                      {isSuper && (
                        <>
                          <button onClick={() => setEditingItem({ ...item })} className="p-1.5 hover:bg-[#F1F5F9] rounded-lg" data-testid={`edit-cons-${item.id}`}><Edit2 className="h-3.5 w-3.5 text-[#475569]" /></button>
                          <button onClick={() => handleDelete(item.id)} className="p-1.5 hover:bg-red-50 rounded-lg" data-testid={`delete-cons-${item.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
                        </>
                      )}
                      {!isSuper && <button onClick={() => addToCart(item)} className="bg-[#0EA5E9] text-white text-xs px-3 py-1.5 rounded-lg font-bold" data-testid={`add-cons-${item.id}`}><Plus className="h-3 w-3" /></button>}
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

/* ═══ Attendance with GPS ═══ */
const AttendanceView = ({ user }) => {
  const [todayRecords, setTodayRecords] = useState([]);
  const [latestStatus, setLatestStatus] = useState('not_checked_in');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('');
  const isStaff = ['franchise_staff', 'franchise_admin'].includes(user.role);
  const canViewAll = ['super_admin', 'franchise_admin'].includes(user.role);

  const toIST = (isoStr) => {
    if (!isoStr) return '-';
    try {
      const d = new Date(isoStr);
      return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    } catch { return isoStr.split('T')[1]?.substring(0, 8) || '-'; }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, allRes] = await Promise.all([
        isStaff ? axios.get(`${API}/franchise/attendance/today`, { withCredentials: true }) : Promise.resolve({ data: { status: 'not_checked_in', records: [] } }),
        axios.get(`${API}/franchise/attendance${dateFilter ? `?date=${dateFilter}` : ''}`, { withCredentials: true }),
      ]);
      const td = todayRes.data;
      setLatestStatus(td.status || 'not_checked_in');
      setTodayRecords(td.records || []);
      setRecords(allRes.data);
    } catch { toast.error('Failed'); }
    setLoading(false);
  }, [isStaff, dateFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getGPS = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('GPS not available')); return; }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      err => reject(err), { enableHighAccuracy: true, timeout: 10000 }
    );
  });

  const handleCheckIn = async () => {
    setGpsLoading(true);
    try {
      const gps = await getGPS();
      await axios.post(`${API}/franchise/attendance/checkin`, { lat: gps.lat, lng: gps.lng }, { withCredentials: true });
      toast.success('Checked in!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed. Enable GPS.');
    }
    setGpsLoading(false);
  };

  const handleCheckOut = async () => {
    setGpsLoading(true);
    try {
      const gps = await getGPS();
      const { data } = await axios.post(`${API}/franchise/attendance/checkout`, { lat: gps.lat, lng: gps.lng }, { withCredentials: true });
      toast.success(`Checked out! Hours: ${data.hours_worked}h`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-out failed. Enable GPS.');
    }
    setGpsLoading(false);
  };

  const totalHoursToday = todayRecords.reduce((sum, r) => sum + (r.hours_worked || 0), 0);

  return (
    <div className="space-y-4">
      {/* Check-in/Check-out for staff */}
      {isStaff && (
        <div className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] rounded-2xl p-5 text-white text-center">
          <p className="text-sm text-white/60 mb-1">{new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}</p>
          {latestStatus === 'not_checked_in' || latestStatus === 'checked_out' ? (
            <>
              <p className="text-lg font-bold mb-1">{todayRecords.length === 0 ? 'Ready to start your day?' : 'Start another session?'}</p>
              {todayRecords.length > 0 && <p className="text-sm text-white/70 mb-2">Sessions today: {todayRecords.length} | Total: {totalHoursToday.toFixed(1)}h</p>}
              <Button onClick={handleCheckIn} disabled={gpsLoading} className="bg-green-500 hover:bg-green-600 text-white rounded-xl h-14 px-8 text-lg font-bold" data-testid="checkin-btn">
                <MapPin className="h-5 w-5 mr-2" /> {gpsLoading ? 'Getting GPS...' : 'Check In'}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-green-300 mb-1">Checked in at {toIST(todayRecords[0]?.check_in)}</p>
              <p className="text-lg font-bold mb-1">Working...</p>
              {todayRecords.length > 1 && <p className="text-sm text-white/70 mb-2">Session {todayRecords.filter(r => r.status === 'checked_out').length + 1} | Total: {totalHoursToday.toFixed(1)}h</p>}
              <Button onClick={handleCheckOut} disabled={gpsLoading} className="bg-red-500 hover:bg-red-600 text-white rounded-xl h-14 px-8 text-lg font-bold" data-testid="checkout-btn">
                <MapPin className="h-5 w-5 mr-2" /> {gpsLoading ? 'Getting GPS...' : 'Check Out'}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Today's sessions summary */}
      {isStaff && todayRecords.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="font-bold text-[#0A2540] text-sm mb-2">Today&apos;s Sessions ({todayRecords.length})</h3>
          <div className="space-y-2">
            {todayRecords.map((r, i) => (
              <div key={r.id} className="flex items-center justify-between text-xs bg-[#F1F5F9] rounded-lg px-3 py-2">
                <span className="text-[#475569]">#{todayRecords.length - i}</span>
                <span className="text-green-600 font-medium">{toIST(r.check_in)}</span>
                <span className="text-red-600 font-medium">{r.check_out ? toIST(r.check_out) : 'Active'}</span>
                <span className="font-bold text-[#0A2540]">{r.hours_worked ? `${r.hours_worked}h` : '...'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter & Download */}
      {canViewAll && (
        <div className="flex gap-2">
          <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="h-11 rounded-xl" placeholder="Filter by date" data-testid="attendance-date-filter" />
          <Button onClick={async () => {
            try {
              const month = dateFilter ? dateFilter.substring(0, 7) : new Date().toISOString().slice(0, 7);
              const response = await axios.get(`${API}/franchise/attendance/export?month=${month}`, { withCredentials: true, responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement('a'); link.href = url;
              link.setAttribute('download', `Attendance_${month}.xlsx`);
              document.body.appendChild(link); link.click(); link.remove();
              toast.success('Downloaded');
            } catch { toast.error('Download failed'); }
          }} className="bg-green-600 hover:bg-green-700 rounded-xl h-11 px-4" data-testid="download-attendance">
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
          <Button onClick={fetchData} variant="outline" size="icon" className="rounded-xl"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      )}

      {/* Records */}
      {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : records.length === 0 ? <p className="text-sm text-[#94A3B8]">No attendance records</p> : (
        <div className="space-y-2">
          {records.map(r => (
            <div key={r.id} className="bg-white rounded-xl p-4 shadow-sm" data-testid={`attendance-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-bold text-[#0A2540]">{r.user_name}</p>
                  <p className="text-xs text-[#94A3B8]">{r.date}</p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${r.status === 'checked_out' ? 'bg-green-100 text-green-700' : r.status === 'checked_in' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                  {r.status === 'checked_out' ? `${r.hours_worked || 0}h` : r.status?.replace('_', ' ')}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-green-50 rounded-lg p-2">
                  <p className="text-green-700 font-medium">Check In</p>
                  <p className="text-[#475569] font-medium">{toIST(r.check_in)}</p>
                  {r.check_in_lat && (
                    <a href={`https://maps.google.com/?q=${r.check_in_lat},${r.check_in_lng}`} target="_blank" rel="noopener noreferrer" className="text-[#0EA5E9] hover:underline flex items-center gap-0.5 mt-0.5">
                      <MapPin className="h-3 w-3" /> View Map
                    </a>
                  )}
                </div>
                <div className="bg-red-50 rounded-lg p-2">
                  <p className="text-red-700 font-medium">Check Out</p>
                  <p className="text-[#475569] font-medium">{r.check_out ? toIST(r.check_out) : '-'}</p>
                  {r.check_out_lat && (
                    <a href={`https://maps.google.com/?q=${r.check_out_lat},${r.check_out_lng}`} target="_blank" rel="noopener noreferrer" className="text-[#0EA5E9] hover:underline flex items-center gap-0.5 mt-0.5">
                      <MapPin className="h-3 w-3" /> View Map
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══ Home Collection Monitoring ═══ */
const HomeCollectionView = ({ user }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ patient_name: '', patient_phone: '', patient_address: '', assigned_to: '', assigned_to_name: '', date: new Date().toISOString().split('T')[0], time_slot: '', tests: '', notes: '' });
  const [gpsLoading, setGpsLoading] = useState(false);
  const canCreate = ['super_admin', 'franchise_admin'].includes(user.role);
  const isFieldStaff = user.role === 'franchise_staff';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/franchise/home-collections`, { withCredentials: true });
      setCollections(data);
    } catch { toast.error('Failed'); }
    setLoading(false);
  }, []);

  const fetchStaff = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/franchise/staff`, { withCredentials: true });
      setStaff(data.filter(s => s.status === 'active'));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); if (canCreate) fetchStaff(); }, [fetchData, canCreate, fetchStaff]);

  const getGPS = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('GPS not available')); return; }
    navigator.geolocation.getCurrentPosition(pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }), err => reject(err), { enableHighAccuracy: true, timeout: 10000 });
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/franchise/home-collections`, form, { withCredentials: true });
      toast.success('Home collection assigned');
      setShowForm(false);
      setForm({ patient_name: '', patient_phone: '', patient_address: '', assigned_to: '', assigned_to_name: '', date: new Date().toISOString().split('T')[0], time_slot: '', tests: '', notes: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleArrive = async (id) => {
    setGpsLoading(true);
    try {
      const gps = await getGPS();
      await axios.put(`${API}/franchise/home-collections/${id}/arrive`, { lat: gps.lat, lng: gps.lng }, { withCredentials: true });
      toast.success('Arrival marked with GPS');
      fetchData();
    } catch (err) { toast.error('Failed. Enable GPS.'); }
    setGpsLoading(false);
  };

  const handleComplete = async (id) => {
    setGpsLoading(true);
    try {
      const gps = await getGPS();
      await axios.put(`${API}/franchise/home-collections/${id}/complete`, { lat: gps.lat, lng: gps.lng }, { withCredentials: true });
      toast.success('Collection completed with GPS');
      fetchData();
    } catch (err) { toast.error('Failed. Enable GPS.'); }
    setGpsLoading(false);
  };

  const STATUS_COLORS = {
    'assigned': 'bg-blue-100 text-blue-700',
    'arrived': 'bg-amber-100 text-amber-700',
    'completed': 'bg-green-100 text-green-700',
  };

  return (
    <div className="space-y-4">
      {canCreate && (
        <Button onClick={() => setShowForm(!showForm)} className="w-full bg-[#0A2540] rounded-xl h-12" data-testid="new-collection-btn">
          <Plus className="h-4 w-4 mr-2" /> Assign Home Collection
        </Button>
      )}

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Patient Name *</Label><Input value={form.patient_name} onChange={e => setForm(p => ({ ...p, patient_name: e.target.value }))} required placeholder="Patient name" className="h-11 rounded-xl mt-1" data-testid="hc-patient" /></div>
            <div><Label>Phone *</Label><Input value={form.patient_phone} onChange={e => setForm(p => ({ ...p, patient_phone: e.target.value }))} required placeholder="9876543210" className="h-11 rounded-xl mt-1" data-testid="hc-phone" /></div>
          </div>
          <div><Label>Address *</Label><Input value={form.patient_address} onChange={e => setForm(p => ({ ...p, patient_address: e.target.value }))} required placeholder="Full address" className="h-11 rounded-xl mt-1" data-testid="hc-address" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="h-11 rounded-xl mt-1" /></div>
            <div><Label>Time Slot</Label><Input value={form.time_slot} onChange={e => setForm(p => ({ ...p, time_slot: e.target.value }))} placeholder="9 AM - 11 AM" className="h-11 rounded-xl mt-1" /></div>
          </div>
          <div><Label>Assign To</Label>
            <select className="w-full h-11 border rounded-xl px-3 text-sm mt-1" value={form.assigned_to} onChange={e => { const s = staff.find(x => x.id === e.target.value); setForm(p => ({ ...p, assigned_to: e.target.value, assigned_to_name: s?.name || '' })); }} data-testid="hc-assign">
              <option value="">Select staff...</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.designation})</option>)}
            </select>
          </div>
          <div><Label>Tests</Label><Input value={form.tests} onChange={e => setForm(p => ({ ...p, tests: e.target.value }))} placeholder="CBC, LFT, Thyroid..." className="h-11 rounded-xl mt-1" /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Special instructions" className="h-11 rounded-xl mt-1" /></div>
          <Button type="submit" className="w-full bg-green-600 rounded-xl h-11" data-testid="hc-create-submit">Assign Collection</Button>
        </form>
      )}

      {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : collections.length === 0 ? <p className="text-sm text-[#94A3B8]">No home collections</p> : (
        <div className="space-y-3">
          {collections.map(c => (
            <div key={c.id} className="bg-white rounded-2xl p-4 shadow-sm" data-testid={`collection-${c.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-bold text-[#0A2540]">{c.patient_name}</p>
                  <a href={`tel:${c.patient_phone}`} className="text-xs text-[#0EA5E9] flex items-center gap-0.5"><Phone className="h-3 w-3" />{c.patient_phone}</a>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-bold capitalize ${STATUS_COLORS[c.status] || 'bg-gray-100'}`}>{c.status}</span>
              </div>
              <p className="text-xs text-[#475569] mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" />{c.patient_address}</p>
              <div className="flex flex-wrap gap-2 text-xs text-[#94A3B8] mb-2">
                <span>{c.date}</span>
                {c.time_slot && <span>| {c.time_slot}</span>}
                {c.assigned_to_name && <span>| {c.assigned_to_name}</span>}
                {c.tests && <span>| {c.tests}</span>}
              </div>

              {/* GPS locations */}
              <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                {c.arrival_lat && (
                  <div className="bg-amber-50 rounded-lg p-2">
                    <p className="text-amber-700 font-medium">Arrival</p>
                    <p className="text-[#475569]">{c.arrival_time?.split('T')[1]?.substring(0, 8)}</p>
                    <a href={`https://maps.google.com/?q=${c.arrival_lat},${c.arrival_lng}`} target="_blank" rel="noopener noreferrer" className="text-[#0EA5E9] hover:underline flex items-center gap-0.5"><MapPin className="h-3 w-3" />Map</a>
                  </div>
                )}
                {c.completion_lat && (
                  <div className="bg-green-50 rounded-lg p-2">
                    <p className="text-green-700 font-medium">Completed</p>
                    <p className="text-[#475569]">{c.completion_time?.split('T')[1]?.substring(0, 8)}</p>
                    <a href={`https://maps.google.com/?q=${c.completion_lat},${c.completion_lng}`} target="_blank" rel="noopener noreferrer" className="text-[#0EA5E9] hover:underline flex items-center gap-0.5"><MapPin className="h-3 w-3" />Map</a>
                  </div>
                )}
              </div>

              {/* Staff actions */}
              {isFieldStaff && c.status === 'assigned' && (
                <Button onClick={() => handleArrive(c.id)} disabled={gpsLoading} className="w-full bg-amber-500 hover:bg-amber-600 rounded-xl h-11 font-bold" data-testid={`arrive-${c.id}`}>
                  <MapPin className="h-4 w-4 mr-2" /> {gpsLoading ? 'Getting GPS...' : 'Mark Arrival (GPS)'}
                </Button>
              )}
              {isFieldStaff && c.status === 'arrived' && (
                <Button onClick={() => handleComplete(c.id)} disabled={gpsLoading} className="w-full bg-green-600 hover:bg-green-700 rounded-xl h-11 font-bold" data-testid={`complete-${c.id}`}>
                  <Check className="h-4 w-4 mr-2" /> {gpsLoading ? 'Getting GPS...' : 'Complete Collection (GPS)'}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══ Orders History with Approval & Invoice ═══ */
const OrdersView = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('orders');
  const isSuper = user.role === 'super_admin';
  const isFranchiseAdmin = user.role === 'franchise_admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, iRes] = await Promise.all([
        axios.get(`${API}/franchise/orders`, { withCredentials: true }),
        axios.get(`${API}/franchise/invoices`, { withCredentials: true }),
      ]);
      setOrders(oRes.data);
      setInvoices(iRes.data);
    } catch { toast.error('Failed'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/franchise/orders/${id}/approve`, {}, { withCredentials: true });
      toast.success('Order approved & wallet debited');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this order?')) return;
    try {
      await axios.put(`${API}/franchise/orders/${id}/reject`, {}, { withCredentials: true });
      toast.success('Order rejected');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleInvoice = async (id) => {
    try {
      const { data } = await axios.post(`${API}/franchise/orders/${id}/invoice`, {}, { withCredentials: true });
      toast.success(`Invoice ${data.invoice_id} created & sent`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const STATUS_COLORS = {
    'pending_approval': 'bg-amber-100 text-amber-700',
    'approved': 'bg-blue-100 text-blue-700',
    'invoiced': 'bg-green-100 text-green-700',
    'rejected': 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button onClick={() => setTab('orders')} className={`px-4 py-2 rounded-xl text-sm font-medium ${tab === 'orders' ? 'bg-[#0A2540] text-white' : 'bg-white text-[#475569]'}`} data-testid="orders-tab">Orders</button>
        <button onClick={() => setTab('invoices')} className={`px-4 py-2 rounded-xl text-sm font-medium ${tab === 'invoices' ? 'bg-[#0A2540] text-white' : 'bg-white text-[#475569]'}`} data-testid="invoices-tab">Invoices</button>
        <Button onClick={fetchData} variant="outline" size="icon" className="rounded-xl ml-auto"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : tab === 'orders' ? (
        orders.length === 0 ? <p className="text-sm text-[#94A3B8]">No orders yet</p> : (
          <div className="space-y-3">
            {orders.map(o => (
              <div key={o.id} className="bg-white rounded-2xl p-4 shadow-sm" data-testid={`order-${o.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#0A2540]">{o.order_id}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${o.order_type === 'test' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>{o.order_type}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-600'}`}>{o.status?.replace('_', ' ')}</span>
                  </div>
                  <span className="font-bold text-[#0A2540]">Rs.{(o.total || 0).toLocaleString()}</span>
                </div>
                <div className="space-y-0.5 mb-2">
                  {(o.items || []).map((item, idx) => (
                    <p key={idx} className="text-xs text-[#475569]">{item.test_name || item.item_name} × {item.qty} = Rs.{(item.price * item.qty).toLocaleString()}</p>
                  ))}
                </div>
                <p className="text-xs text-[#94A3B8]">{o.created_at?.split('T')[0]} | by {o.ordered_by}</p>
                {o.invoice_id && <p className="text-xs text-green-600 font-medium mt-1">Invoice: {o.invoice_id}</p>}

                {/* Franchise Admin approves staff orders */}
                {isFranchiseAdmin && o.status === 'pending_approval' && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => handleApprove(o.id)} className="flex-1 bg-green-600 text-white text-sm py-2 rounded-xl font-bold" data-testid={`approve-order-${o.id}`}>Approve & Debit Wallet</button>
                    <button onClick={() => handleReject(o.id)} className="px-4 bg-red-100 text-red-700 text-sm py-2 rounded-xl font-bold" data-testid={`reject-order-${o.id}`}>Reject</button>
                  </div>
                )}

                {/* Super Admin creates invoice for approved orders */}
                {isSuper && o.status === 'approved' && !o.invoice_id && (
                  <button onClick={() => handleInvoice(o.id)} className="w-full mt-3 bg-[#0A2540] text-white text-sm py-2.5 rounded-xl font-bold flex items-center justify-center gap-2" data-testid={`invoice-order-${o.id}`}>
                    <CreditCard className="h-4 w-4" /> Generate & Send Invoice
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        invoices.length === 0 ? <p className="text-sm text-[#94A3B8]">No invoices yet</p> : (
          <div className="space-y-3">
            {invoices.map(inv => (
              <div key={inv.id} className="bg-white rounded-2xl p-4 shadow-sm border-l-4 border-green-500" data-testid={`invoice-${inv.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-bold text-[#0A2540]">{inv.invoice_id}</span>
                    <span className="text-xs text-[#94A3B8] ml-2">({inv.order_id})</span>
                  </div>
                  <span className="font-bold text-green-600">Rs.{(inv.total || 0).toLocaleString()}</span>
                </div>
                {isSuper && <p className="text-xs text-[#475569] mb-1">To: {inv.franchise_name} {inv.franchise_gst ? `| GST: ${inv.franchise_gst}` : ''}</p>}
                <div className="space-y-0.5 mb-2">
                  {(inv.items || []).map((item, idx) => (
                    <p key={idx} className="text-xs text-[#475569]">{item.test_name || item.item_name} × {item.qty} = Rs.{(item.price * item.qty).toLocaleString()}</p>
                  ))}
                </div>
                <p className="text-xs text-[#94A3B8]">{inv.created_at?.split('T')[0]} | {inv.order_type}</p>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

/* ═══ Change Password ═══ */
const ChangePasswordView = () => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) { toast.error('Passwords do not match'); return; }
    if (form.new_password.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/franchise/change-password`, { current_password: form.current_password, new_password: form.new_password }, { withCredentials: true });
      toast.success('Password changed successfully!');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2.5 bg-[#0A2540]/10 rounded-xl"><KeyRound className="h-5 w-5 text-[#0A2540]" /></div>
          <div><h2 className="font-bold text-[#0A2540] text-lg">Change Password</h2><p className="text-xs text-[#94A3B8]">Enter your current password and choose a new one</p></div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label className="text-sm">Current Password *</Label><Input type="password" value={form.current_password} onChange={e => setForm(p => ({ ...p, current_password: e.target.value }))} required className="h-11 rounded-xl mt-1" data-testid="current-password" /></div>
          <div><Label className="text-sm">New Password *</Label><Input type="password" value={form.new_password} onChange={e => setForm(p => ({ ...p, new_password: e.target.value }))} required minLength={6} className="h-11 rounded-xl mt-1" data-testid="new-password" /></div>
          <div><Label className="text-sm">Confirm New Password *</Label><Input type="password" value={form.confirm_password} onChange={e => setForm(p => ({ ...p, confirm_password: e.target.value }))} required className="h-11 rounded-xl mt-1" data-testid="confirm-password" /></div>
          <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] rounded-xl h-12 font-bold" data-testid="change-password-submit">
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
      </div>
    </div>
  );
};

/* ═══ Password Reset Requests (Super Admin) ═══ */
const PasswordResetsView = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPasswords, setNewPasswords] = useState({});

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/franchise/password-reset-requests`, { withCredentials: true });
      setRequests(data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = async (req) => {
    const pw = newPasswords[req.id] || '';
    if (!pw || pw.length < 6) { toast.error('Enter a new password (min 6 chars)'); return; }
    try {
      await axios.post(`${API}/franchise/approve-password-reset/${req.id}`, { new_password: pw }, { withCredentials: true });
      toast.success(`Password reset approved for ${req.email}`);
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleReject = async (req) => {
    if (!window.confirm(`Reject reset request from ${req.email}?`)) return;
    try {
      await axios.post(`${API}/franchise/reject-password-reset/${req.id}`, {}, { withCredentials: true });
      toast.success('Request rejected');
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const pending = requests.filter(r => r.status === 'pending');
  const resolved = requests.filter(r => r.status !== 'pending');

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-[#0A2540]">Password Reset Requests</h2>

      {pending.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-bold text-amber-600">Pending ({pending.length})</p>
          {pending.map(req => (
            <div key={req.id} className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-amber-400" data-testid={`reset-req-${req.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-bold text-[#0A2540]">{req.user_name}</p>
                  <p className="text-xs text-[#475569]">{req.email} | {req.role}</p>
                  {req.reason && <p className="text-xs text-[#94A3B8] mt-0.5">Reason: {req.reason}</p>}
                </div>
                <span className="text-xs text-[#94A3B8]">{req.created_at?.split('T')[0]}</span>
              </div>
              <div className="flex gap-2 items-end mt-3">
                <div className="flex-1">
                  <Label className="text-xs">Set New Password</Label>
                  <Input type="text" placeholder="Min 6 characters" value={newPasswords[req.id] || ''}
                    onChange={e => setNewPasswords(p => ({ ...p, [req.id]: e.target.value }))}
                    className="h-10 rounded-xl mt-0.5 text-sm" data-testid={`new-pw-${req.id}`} />
                </div>
                <Button onClick={() => handleApprove(req)} className="bg-green-600 hover:bg-green-700 rounded-xl h-10 px-4 text-sm" data-testid={`approve-${req.id}`}>
                  Approve
                </Button>
                <Button onClick={() => handleReject(req)} variant="outline" className="rounded-xl h-10 px-4 text-sm border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-${req.id}`}>
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : pending.length === 0 && <p className="text-sm text-[#94A3B8]">No pending reset requests</p>}

      {resolved.length > 0 && (
        <div className="space-y-2 mt-6">
          <p className="text-sm font-bold text-[#475569]">History ({resolved.length})</p>
          {resolved.map(req => (
            <div key={req.id} className="bg-white rounded-xl p-3 shadow-sm flex items-center justify-between">
              <div>
                <p className="font-medium text-sm text-[#0A2540]">{req.user_name} ({req.email})</p>
                <p className="text-xs text-[#94A3B8]">{req.created_at?.split('T')[0]} | by {req.resolved_by || '-'}</p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${req.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {req.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══ Notifications Panel ═══ */
const NotificationsPanel = ({ onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/franchise/notifications`, { withCredentials: true });
        setNotifications(data);
        await axios.put(`${API}/franchise/notifications/mark-read`, {}, { withCredentials: true });
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const NOTIF_COLORS = {
    success: 'border-l-green-500 bg-green-50',
    error: 'border-l-red-500 bg-red-50',
    warning: 'border-l-amber-500 bg-amber-50',
    info: 'border-l-blue-500 bg-blue-50',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/30 p-4 pt-16" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm max-h-[70vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="notifications-panel">
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-bold text-[#0A2540]">Notifications</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-[#475569]" /></button>
        </div>
        <div className="p-3 space-y-2">
          {loading ? <p className="text-sm text-[#94A3B8] p-4">Loading...</p> : notifications.length === 0 ? <p className="text-sm text-[#94A3B8] p-4">No notifications</p> : (
            notifications.map(n => (
              <div key={n.id} className={`border-l-4 rounded-xl p-3 ${NOTIF_COLORS[n.type] || NOTIF_COLORS.info}`}>
                <p className="text-sm font-bold text-[#0A2540]">{n.title}</p>
                <p className="text-xs text-[#475569]">{n.message}</p>
                <p className="text-xs text-[#94A3B8] mt-1">{n.created_at?.split('T')[0]} {n.created_at?.split('T')[1]?.substring(0, 5)}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══ Reports Module ═══ */
const ReportsView = ({ user }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [downloading, setDownloading] = useState('');

  const downloadReport = async (endpoint, filename) => {
    setDownloading(endpoint);
    try {
      const response = await axios.get(`${API}/franchise/reports/${endpoint}?month=${selectedMonth}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `${filename}_${selectedMonth}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch { toast.error('Download failed'); }
    setDownloading('');
  };

  const reports = [
    { id: 'wallet-transactions', name: 'Wallet Transactions', desc: 'All credits & debits with descriptions', icon: Wallet, color: 'bg-green-100 text-green-700' },
    { id: 'consumable-purchases', name: 'Consumable Purchases', desc: 'All consumable orders with items and totals', icon: Package, color: 'bg-purple-100 text-purple-700' },
    { id: 'home-collections', name: 'Home Collection Performance', desc: 'Visits, GPS data, completion times', icon: MapPin, color: 'bg-teal-100 text-teal-700' },
    { id: 'monthly-summary', name: 'Monthly Business Summary', desc: 'Wallet, orders, attendance, collections overview', icon: FileSpreadsheet, color: 'bg-blue-100 text-blue-700' },
    { id: 'activity-log', name: 'Franchise Activity Log', desc: 'All actions: recharges, orders, attendance, collections', icon: Clock, color: 'bg-amber-100 text-amber-700' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Label className="whitespace-nowrap font-semibold text-[#0A2540]">Month:</Label>
        <Input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="h-11 rounded-xl w-auto" data-testid="report-month" />
      </div>
      <p className="text-xs text-[#94A3B8]">Attendance report is available in the Attendance tab.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {reports.map(r => {
          const Icon = r.icon;
          const isDownloading = downloading === r.id;
          return (
            <button key={r.id} onClick={() => downloadReport(r.id, r.name.replace(/\s/g, '_'))}
              disabled={isDownloading}
              className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-all text-left flex items-start gap-4 disabled:opacity-50"
              data-testid={`report-${r.id}`}
            >
              <div className={`p-3 rounded-xl ${r.color} flex-shrink-0`}><Icon className="h-6 w-6" /></div>
              <div className="flex-1">
                <h3 className="font-bold text-[#0A2540] text-sm">{r.name}</h3>
                <p className="text-xs text-[#475569] mt-0.5">{r.desc}</p>
                <p className="text-xs text-[#0EA5E9] font-medium mt-2 flex items-center gap-1">
                  <Download className="h-3 w-3" /> {isDownloading ? 'Downloading...' : 'Download Excel'}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ═══ Main Franchise Portal ═══ */
const FranchisePortal = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [activeModule, setActiveModule] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => {
        const d = res.data;
        if (['admin', 'franchise_admin', 'franchise_staff'].includes(d.role)) {
          const mapped = { ...d, role: d.role === 'admin' ? 'super_admin' : d.role };
          setUser(mapped);
        }
        setChecking(false);
      })
      .catch(() => { setUser(null); setChecking(false); });
  }, []); // eslint-disable-line

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/franchise/dashboard`, { withCredentials: true });
      setStats(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (user) fetchDashboard(); }, [user, fetchDashboard]);

  // Fetch unread notification count
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const { data } = await axios.get(`${API}/franchise/notifications/unread-count`, { withCredentials: true });
        setUnreadCount(data.count || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  if (checking) return <div className="min-h-screen bg-[#0A2540] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white" /></div>;
  if (!user) return <FranchiseLogin onLogin={setUser} />;

  const isSuper = user.role === 'super_admin';
  const isStaff = user.role === 'franchise_staff';

  const modules = isSuper ? [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'create-franchise', label: 'New Franchise', icon: Plus },
    { id: 'tests', label: 'Test Catalog', icon: FlaskRound },
    { id: 'consumables', label: 'Consumables', icon: Package },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'wallet', label: 'Wallet', icon: Wallet },
    { id: 'attendance', label: 'Attendance', icon: Clock },
    { id: 'home-collections', label: 'Home Collections', icon: MapPin },
    { id: 'staff', label: 'Staff', icon: Users },
    { id: 'reports', label: 'Reports', icon: FileSpreadsheet },
    { id: 'password-resets', label: 'Password Resets', icon: KeyRound },
    { id: 'branches', label: 'Branch Profiles', icon: Building2 },
  ] : isStaff ? [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'tests', label: 'Order Tests', icon: FlaskRound },
    { id: 'consumables', label: 'Consumables', icon: Package },
    { id: 'orders', label: 'My Orders', icon: ShoppingCart },
    { id: 'attendance', label: 'Attendance', icon: Clock },
    { id: 'home-collections', label: 'Collections', icon: MapPin },
    { id: 'change-password', label: 'Change Password', icon: KeyRound },
  ] : [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'tests', label: 'Order Tests', icon: FlaskRound },
    { id: 'consumables', label: 'Consumables', icon: Package },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'wallet', label: 'Wallet', icon: Wallet },
    { id: 'attendance', label: 'Attendance', icon: Clock },
    { id: 'home-collections', label: 'Home Collections', icon: MapPin },
    { id: 'staff', label: 'Staff', icon: Users },
    { id: 'reports', label: 'Reports', icon: FileSpreadsheet },
    { id: 'change-password', label: 'Change Password', icon: KeyRound },
    { id: 'profile', label: 'Branch Profile', icon: Building2 },
  ];

  const navigate = (mod, data) => {
    if (data) setSelectedFranchise(data);
    setActiveModule(mod);
  };

  const renderModule = () => {
    switch (activeModule) {
      case 'dashboard':
        return isSuper
          ? <SuperAdminDashboard stats={stats} onNavigate={navigate} />
          : <FranchiseAdminDashboard stats={stats} />;
      case 'create-franchise':
        return <CreateFranchise onSave={() => { fetchDashboard(); setActiveModule('dashboard'); }} onCancel={() => setActiveModule('dashboard')} />;
      case 'wallet':
        return <WalletView user={user} franchiseId={user.franchise_id} />;
      case 'staff':
        return <StaffView user={user} />;
      case 'tests':
        return <TestCatalog user={user} />;
      case 'consumables':
        return <ConsumablesStore user={user} />;
      case 'orders':
        return <OrdersView user={user} />;
      case 'attendance':
        return <AttendanceView user={user} />;
      case 'home-collections':
        return <HomeCollectionView user={user} />;
      case 'reports':
        return <ReportsView user={user} />;
      case 'profile':
        return stats?.franchise ? <BranchProfile franchise={stats.franchise} onSave={fetchDashboard} /> : <p className="text-[#94A3B8]">Loading...</p>;
      case 'branches':
        return <AllBranchProfiles />;
      case 'branch-detail':
        return selectedFranchise ? <BranchProfile franchise={selectedFranchise} readOnly /> : null;
      case 'change-password':
        return <ChangePasswordView />;
      case 'password-resets':
        return <PasswordResetsView />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-[#0A2540] text-white">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <p className="font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>AMC247 Franchise</p>
            <p className="text-xs text-sky-300 capitalize">{isSuper ? 'Super Admin' : user.role?.replace('_', ' ')}</p>
            {!isSuper && stats?.franchise && <p className="text-xs text-white/50 mt-0.5">{stats.franchise.name}</p>}
          </div>
          <button onClick={() => { setShowNotifications(true); setUnreadCount(0); }} className="relative text-white/60 hover:text-white" data-testid="desktop-notif-bell">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{unreadCount}</span>}
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {modules.map(m => {
            const Icon = m.icon;
            return (
              <button key={m.id} onClick={() => setActiveModule(m.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeModule === m.id ? 'bg-white/15 text-white' : 'text-slate-300 hover:bg-white/5'}`}
                data-testid={`fr-nav-${m.id}`}>
                <Icon className="h-5 w-5" /><span>{m.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm text-slate-300 hover:bg-white/5" data-testid="franchise-logout">
            <LogOut className="h-5 w-5" /><span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#0A2540] text-white flex flex-col">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <p className="font-bold text-sm">AMC247 Franchise</p>
                <p className="text-xs text-sky-300 capitalize">{isSuper ? 'Super Admin' : user.role?.replace('_', ' ')}</p>
              </div>
              <button onClick={() => setSidebarOpen(false)}><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex-1 p-3 space-y-1">
              {modules.map(m => {
                const Icon = m.icon;
                return (
                  <button key={m.id} onClick={() => { setActiveModule(m.id); setSidebarOpen(false); }}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium ${activeModule === m.id ? 'bg-white/15' : 'text-slate-300 hover:bg-white/5'}`}>
                    <Icon className="h-5 w-5" /><span>{m.label}</span>
                  </button>
                );
              })}
            </nav>
            <div className="p-3 border-t border-white/10">
              <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm text-slate-300">
                <LogOut className="h-5 w-5" /><span>Logout</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="lg:hidden bg-white shadow-sm px-4 py-3 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)}><Menu className="h-6 w-6 text-[#0A2540]" /></button>
          <h1 className="font-bold text-[#0A2540] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {modules.find(m => m.id === activeModule)?.label || 'Dashboard'}
          </h1>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowNotifications(true)} className="relative text-[#475569]" data-testid="mobile-notif-bell">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{unreadCount}</span>}
            </button>
            <button onClick={handleLogout} className="text-[#475569]"><LogOut className="h-5 w-5" /></button>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <div className="max-w-4xl mx-auto">
            {activeModule === 'branch-detail' && (
              <button onClick={() => setActiveModule('dashboard')} className="flex items-center gap-1 text-[#475569] text-sm mb-3 hover:text-[#0A2540]">
                <ArrowLeft className="h-4 w-4" /> Back to Dashboard
              </button>
            )}
            {renderModule()}
          </div>
        </main>
      </div>
      {showNotifications && <NotificationsPanel onClose={() => { setShowNotifications(false); setUnreadCount(0); }} />}
    </div>
  );
};

export default FranchisePortal;
